import { Flex, Popconfirm, Skeleton, Tooltip, Typography } from 'antd';
import styles from './autoReplyStatusIndicator.module.less';
import { AiIcon } from '@copilot/common/components/ai/aiIcon/aiIcon';
import { PlayCircleFilled, PauseCircleFilled, QuestionCircleOutlined } from '@ant-design/icons';
import { useFeatureFlags } from '@copilot/common/hooks/useFeatureFlags/useFeatureFlags';
import { AiSwitch } from '@copilot/common/components/ai/aiSwitch/aiSwitch';
import { useState } from 'react';

const { Text } = Typography;

type AutoReplyStatusIndicatorProps = {
    /**
     * Whether auto reply is enabled or disabled
     */
    isEnabled: boolean;

    /**
     * Whether auto reply is paused
     */
    isPaused: boolean;

    /**
     * Callback to update the auto reply status
     */
    onChange?: (isEnabled: boolean) => unknown;

    /**
     * Whether the auto reply toggle is updating
     */
    isUpdating?: boolean;

    /**
     * Whether the auto reply toggle is loading
     */
    isLoading?: boolean;

    /**
     * Whether to hide the auto reply toggle
     */
    hideToggle?: boolean;

    /**
     * Whether to use popconfirm
     */
    usePopconfirm?: boolean;
};

/**
 * Auto reply status indicator component
 */
function AutoReplyStatusIndicator({
    isEnabled,
    isPaused,
    onChange,
    isUpdating,
    isLoading = false,
    hideToggle = false,
    usePopconfirm = false,
}: AutoReplyStatusIndicatorProps) {
    const { isAutoReplyBetaV1Enabled, isAutoReplyConnectionGatingEnabled } = useFeatureFlags();
    const [isPopconfirmOpen, setIsPopconfirmOpen] = useState(false);

    function handleChange(enabled: boolean) {
        if (!usePopconfirm) {
            return onChange?.(enabled);
        }

        if (enabled) {
            setIsPopconfirmOpen(true);
        } else {
            onChange?.(enabled);
        }
        return;
    }

    function handleConfirm() {
        setIsPopconfirmOpen(false);
        onChange?.(true);
    }

    function handleCancel() {
        setIsPopconfirmOpen(false);
    }

    if (!isAutoReplyBetaV1Enabled) {
        return null;
    }

    if (isLoading) {
        return (
            <Skeleton
                className={styles.autoReplySkeleton}
                title={false}
                active
                paragraph={{ rows: 1 }}
            />
        );
    }

    const stateIcon = !isPaused ? (
        <PlayCircleFilled className={styles.enabledIcon} />
    ) : (
        <PauseCircleFilled className={styles.disabledIcon} />
    );

    const tooltipContent = !isPaused ? (
        'Next reply scheduled'
    ) : (
        <>
            <span className={styles.autoReplyPausedTooltipListTitle}>
                Why Auto Reply is paused:
            </span>
            <ul className={styles.autoReplyPausedTooltipList}>
                <li>Prospect responded with strong hostility</li>
                <li>Reply came in while Auto Reply was turned off</li>
                <li>Auto Reply ended based on conversation signals.</li>
            </ul>
        </>
    );

    const indicator = (
        <Flex align="center" gap={8}>
            {isAutoReplyConnectionGatingEnabled && !hideToggle && (
                <AiSwitch checked={isEnabled} onChange={handleChange} loading={isUpdating} />
            )}
            {stateIcon}
            <Text className={styles.autoReplyText}>Auto Reply</Text>
            <AiIcon noSpace />
            <Tooltip title={tooltipContent} placement="top">
                <QuestionCircleOutlined />
            </Tooltip>
        </Flex>
    );

    return usePopconfirm ? (
        <Popconfirm
            title="Reactivate Auto Reply"
            description="We'll send the message shown and let Auto Reply continue the conversation."
            okText="Yes"
            cancelText="No"
            open={isPopconfirmOpen}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            align={{
                offset: [0, -20],
                targetOffset: ['45%', '0%'],
            }}
        >
            {indicator}
        </Popconfirm>
    ) : (
        indicator
    );
}

export default AutoReplyStatusIndicator;
